/**
 * Client-side rules for seller warranty SKUs. Mirrors the backend's
 * `src/warranty-product/dto/create-warranty-product.dto.ts` (same limits, same
 * messages) so sellers see the problem before the request is sent. Keep both
 * in sync.
 */

const NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{M}\p{N} &'’.,()/+-]*$/u;
/** Plain decimal with at most 2 decimals — rejects "1e5", "-1", "10.555". */
const PRICE_PATTERN = /^\d+(?:\.\d{1,2})?$/;

export const WARRANTY_NAME_MAX_LENGTH = 120;
export const WARRANTY_DESCRIPTION_MAX_LENGTH = 500;
export const WARRANTY_MAX_PRICE = 1_000_000;

export interface WarrantyFormValues {
  name: string;
  price: string;
  warrantyId: string;
  warrantyTypeId: string;
  warrantyProviderId: string;
  description: string;
}

export type WarrantyFormErrors = Partial<Record<keyof WarrantyFormValues, string>>;

function validateName(value: string): string | undefined {
  const name = value.trim();
  if (!name) return "Name is required.";
  if (name.length > WARRANTY_NAME_MAX_LENGTH) {
    return `Name can be at most ${WARRANTY_NAME_MAX_LENGTH} characters.`;
  }
  if (!NAME_PATTERN.test(name)) {
    return "Name can only contain letters, numbers, spaces and & . , ' ( ) / + -";
  }
  return undefined;
}

function validatePrice(value: string): string | undefined {
  const price = value.trim();
  if (!price) return "Price is required.";
  if (!PRICE_PATTERN.test(price) || Number(price) > WARRANTY_MAX_PRICE) {
    return `Price must be a number between 0 and ${WARRANTY_MAX_PRICE.toLocaleString("en-US")} QAR with at most 2 decimals.`;
  }
  return undefined;
}

/** Returns one message per invalid field; an empty object means the form is valid. */
export function validateWarrantyForm(values: WarrantyFormValues): WarrantyFormErrors {
  const errors: WarrantyFormErrors = {
    name: validateName(values.name),
    price: validatePrice(values.price),
    warrantyId: values.warrantyId ? undefined : "Period is required.",
    warrantyTypeId: values.warrantyTypeId ? undefined : "Type is required.",
    warrantyProviderId: values.warrantyProviderId ? undefined : "Provider is required.",
    description:
      values.description.trim().length > WARRANTY_DESCRIPTION_MAX_LENGTH
        ? `Description can be at most ${WARRANTY_DESCRIPTION_MAX_LENGTH} characters.`
        : undefined,
  };
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message),
  ) as WarrantyFormErrors;
}
